import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { type Tag } from '../useTags';
import { useToastMutation } from '../utils/mutation';
import { onAngularEvent } from '../../services/angular-bridge';
import { notifySuccess, notifyError } from '../../services/notify';

export interface ExportAccount {
  id: string;
  email: string;
  fullName: string;
}

export interface ExportJob {
  percentageComplete: number;
  status?: string;
}

export interface ExportTag {
  id: string;
  name: string;
  value?: string;
  color?: string;
  type?: number | string;
}

export interface ExportTagFilter {
  and: ExportTag[];
  or: ExportTag[];
  not: ExportTag[];
}

export interface Export {
  id: string;
  projectId: string;
  name: string;
  status: string;
  account: ExportAccount;
  outputTypes: string[];
  startBates: string;
  endBates: string;
  createdAtString: string;
  path: string | null;
  hadExportException: boolean;
  directDownloadPath: string | null;
  job: ExportJob | null;
  // Detail fields present on list and info view
  colorSetting?: string;
  colorTag?: Tag | null;
  includeResourcesWithProcessingExceptions?: boolean;
  includeResourcesWithZeroPages?: boolean;
  includeDuplicateResources?: boolean;
  includeRedactedResources?: boolean;
  keepPageOrientation?: boolean;
  collectionNames?: string[];
  tags?: ExportTagFilter;
  containerExportTag?: ExportTag | null;
  loadFileFormatNames?: string[];
  spreadsheetPlaceholder?: boolean;
  presentationPlaceholder?: boolean;
  tagPlaceholder?: ExportTag | null;
  prefix?: string;
  delimiter?: string;
  startValue?: number;
  zeroPaddingLength?: number;
  filesPerDirectory?: number;
  collectionIds?: string[];
  loadFileFormatIds?: string[];
  exportException?: string;
  exportTag?: ExportTag | null;
  virusPlaceholderText?: string;
  nativeFormatPlaceholderText?: string;
  failedToImagePlaceholderText?: string;
  tagPlaceholderEnabled?: boolean;
  outputOriginalNativePaths?: boolean;
  includeContainerExportTag?: boolean;
  southWestEndorsement?: ExportTag[];
  southEndorsement?: ExportTag[];
  southEastEndorsement?: ExportTag[];
}

export function useExports(projectId: string) {
  return useQuery<Export[]>({
    queryKey: ['exports', projectId],
    queryFn: () => api.get<Export[]>(`/project/${projectId}/export`),
    enabled: !!projectId,
  });
}

export interface ExportStatisticsRequest {
  projectId: string;
  collectionIds: string[];
  tags: { and: string[]; or: string[]; not: string[] };
  containerExportTag: { id: string; name: string } | null;
  includeContainerExportTag: boolean;
  includeRedactedResources: boolean;
  includeResourcesWithZeroPages: boolean;
  includeResourcesWithProcessingExceptions: boolean;
  includeDuplicateResources: boolean;
}

export interface ExportStatistics {
  exceptions: number;
  duplicates: number;
  redacted: number;
  zeroPages: number;
  resourceCount: number;
  pageCount: number;
  size: number;
}

export function useExportStatistics(projectId: string) {
  return useMutation<ExportStatistics, Error, ExportStatisticsRequest>({
    mutationFn: (req) => api.post<ExportStatistics>(`/project/${projectId}/export/statistics`, req),
  });
}

export function useCreateExport(projectId: string) {
  return useToastMutation(
    ['exports', projectId],
    (body: Record<string, unknown>) => api.post(`/project/${projectId}/export`, body),
    {
      success: () => ({ title: 'Export in progress', description: 'You will be notified when the export is complete.', timeout: 5000 }),
    },
  );
}

export function useReplayExport(projectId: string) {
  return useToastMutation(
    ['exports', projectId],
    (exportId: string) => api.post(`/project/${projectId}/export/${exportId}/replay`, {}),
    {
      success: () => ({ title: 'Export Replay started', description: '', timeout: 2500 }),
    }
  );
}

export function useRenameExport(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (exp: Export) =>
      api.put<Export>(`/project/${projectId}/export/${exp.id}`, exp),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exports', projectId] });
    },
  });
}

// Toast + refresh when an export finishes or fails (FeedService.ExportComplete / ExportFailed).
// Mount once on the Exports tab.
export function useExportEventToasts(projectId: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const offComplete = onAngularEvent('FeedService.ExportComplete', (message: { name: string; directDownloadPath: string }) => {
      notifySuccess({
        title: 'Export complete',
        description: `Export: ${message.name} has completed.`,
        buttons: `<a class="btn btn-success" href="${message.directDownloadPath}"><i class="icon-download-alt"></i> Download</a>`,
      });
      queryClient.invalidateQueries({ queryKey: ['exports', projectId] });
    });
    // Asymmetry is intentional (mirrors Angular): ExportComplete uses message.name, ExportFailed uses message.exportName.
    const offFailed = onAngularEvent('FeedService.ExportFailed', (message: { exportName: string }) => {
      notifyError({ title: 'Export failed', description: `Export: ${message.exportName} has failed.` });
      queryClient.invalidateQueries({ queryKey: ['exports', projectId] });
    });
    return () => { offComplete(); offFailed(); };
  }, [projectId, queryClient]);
}
